import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Checkbox,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Box,
  Badge,
  CheckboxGroup,
} from '@chakra-ui/react';
import { FiUsers, FiHeart, FiMapPin } from 'react-icons/fi';
import { RegistrationData } from '../index';

interface DiversityClassificationsProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const DiversityClassifications: React.FC<DiversityClassificationsProps> = ({
  data,
  updateData,
}) => {
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const infoBg = useColorModeValue('blue.50', 'blue.900');

  const updateDiversityClassifications = (field: string, value: any) => {
    updateData({
      ...data,
      diversityClassifications: {
        ...data.diversityClassifications,
        [field]: value,
      },
    });
  };

  const diversityDesignations = [
    { value: 'disabled', label: 'Disabled', color: 'orange' },
    { value: 'disabled-veteran-dvbe', label: 'Disabled Veteran (DVBE)', color: 'purple' },
    { value: 'lgbtq', label: 'LGBTQ+', color: 'pink' },
    { value: 'minority-mbe', label: 'Minority (MBE)', color: 'blue' },
    { value: 'veteran', label: 'Veteran', color: 'blue' },
    { value: 'women-owned-wbe', label: 'Women-Owned (WBE)', color: 'green' },
    { value: 'none', label: 'None', color: 'gray' },
    { value: 'other', label: 'Other', color: 'yellow' },
  ];

  return (
    <VStack spacing={8} align="stretch">
      {/* Introduction */}
      <Box bg={infoBg} p={6} borderRadius="lg">
        <VStack align="start" spacing={3}>
          <HStack spacing={3}>
            <Icon as={FiUsers} color="blue.500" boxSize={5} />
            <Text fontSize="lg" fontWeight="semibold">
              Diversity Program Support
            </Text>
          </HStack>
          <Text color="gray.700" fontSize="sm">
            To support Tangram's Diversity program, please assist us in assuring that we understand 
            the certifications that your company has, whether you support UNICOR and Ability One 
            suppliers and if you are located in a HUB Zone.
          </Text>
        </VStack>
      </Box>

      {/* Diversity Designations */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiHeart} color="purple.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Diversity Designations
              </Text>
            </HStack>

            <Text color="gray.600" fontSize="sm">
              Please mark all classifications that your company is certified as being owned 
              and operated under the Diverse designation.
            </Text>

            <FormControl>
              <FormLabel fontWeight="medium">
                Select Your Designations
              </FormLabel>
              <CheckboxGroup
                value={data.diversityClassifications?.designations || []}
                onChange={(values) => updateDiversityClassifications('designations', values)}
              >
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {diversityDesignations.map((designation) => (
                    <Checkbox 
                      key={designation.value} 
                      value={designation.value}
                      colorScheme={designation.color}
                    >
                      <HStack spacing={2}>
                        <Text>{designation.label}</Text>
                        <Badge colorScheme={designation.color} size="sm" variant="subtle">
                          {designation.value.includes('mbe') ? 'MBE' : 
                           designation.value.includes('wbe') ? 'WBE' :
                           designation.value.includes('dvbe') ? 'DVBE' : ''}
                        </Badge>
                      </HStack>
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </CheckboxGroup>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Supplier Programs */}
      <Card>
        <CardBody p={8}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={3}>
              <Icon as={FiMapPin} color="green.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="semibold">
                Supplier Programs & Location
              </Text>
            </HStack>

            <VStack spacing={4} align="stretch">
              <FormControl>
                <Checkbox
                  isChecked={data.diversityClassifications?.unicorSuppliers || false}
                  onChange={(e) => updateDiversityClassifications('unicorSuppliers', e.target.checked)}
                  colorScheme="blue"
                >
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">
                      UNICOR (Federal Prison Industries) Suppliers
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Please indicate whether your company sources materials from UNICOR suppliers
                    </Text>
                  </VStack>
                </Checkbox>
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={data.diversityClassifications?.abilityOneSuppliers || false}
                  onChange={(e) => updateDiversityClassifications('abilityOneSuppliers', e.target.checked)}
                  colorScheme="green"
                >
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">
                      Ability One (NIB/NISH) Suppliers
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Please indicate whether your company sources materials from Ability One suppliers
                    </Text>
                  </VStack>
                </Checkbox>
              </FormControl>

              <FormControl>
                <Checkbox
                  isChecked={data.diversityClassifications?.hubZone || false}
                  onChange={(e) => updateDiversityClassifications('hubZone', e.target.checked)}
                  colorScheme="purple"
                >
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="medium">
                      Historically Under-Utilized Business (HUB) Zone
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Please indicate whether your company is operating in and employing people in HUB Zones
                    </Text>
                  </VStack>
                </Checkbox>
              </FormControl>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default DiversityClassifications; 